import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  FileSignature,
  FileText,
  Link2,
  MoreHorizontal,
  Pencil,
  Plus,
  Repeat,
  Search,
  Send,
  Trash2,
  TrendingUp,
  XCircle,
} from "lucide-react"

import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import {
  STATUS_LABELS,
  fmtCurrency,
  fmtDate,
  propostaFromRow,
  publicUrl,
  resumoModalidade,
  salvarProposta,
  totaisDosItens,
  uid,
} from "@/lib/propostas"
import { cn } from "@/lib/utils"
import type { Proposta, StatusProposta } from "@/types/comercial"

import PropostaEditor from "./PropostaEditor"
import { ModalidadesResumo, StatusPill } from "./propostaUi"

// ── Mock initial data (usado só quando o banco está vazio/inacessível) ────────

const MOCK_PROPOSTAS: Proposta[] = [
  {
    id: "p1",
    titulo: "Proposta Comercial - ItaCondo",
    clienteNome: "João Espindola",
    clienteEmail: "joao.espindola@kpisolutions.com.br",
    clienteTelefone: "21998231720",
    clienteEmpresa: "Condomínio Residencial Dendezê",
    status: "enviada",
    itens: [
      { id: "i1", descricao: "Assinatura Mensal Software ItaCondo Portaria & Controle de Acesso", quantidade: 1, valorUnitario: 250, valorTotal: 250 },
      { id: "i2", descricao: "Instalação física de Câmeras de Segurança Dome IP 4MP", quantidade: 4, valorUnitario: 800, valorTotal: 3200 },
      { id: "i3", descricao: "Configuração de Gravador NVR e Infraestrutura de Rede", quantidade: 1, valorUnitario: 1550, valorTotal: 1550 },
    ],
    valorTotal: 5000,
    desconto: 0,
    valorFinal: 5000,
    validadeAte: "2026-12-31",
    escopo: "Prestação de serviços de implantação de rede, configuração de câmeras IP de segurança monitoradas em tempo real por IA e assinatura de licença do software ItaCondo.",
    observacoes: "Pagamento facilitado em até 12x no boleto do condomínio. Instalação concluída em até 5 dias úteis.",
    termos: "Contrato mínimo de 12 meses para o fornecimento do software e manutenção das câmeras.",
    token: "LgoSE3jj",
    historico: [
      { id: "h1", tipo: "criada", data: "2026-06-25T10:00:00Z", descricao: "Proposta criada pelo administrador" },
      { id: "h2", tipo: "enviada", data: "2026-06-25T11:00:00Z", descricao: "Proposta enviada ao cliente" },
    ],
    createdAt: "2026-06-25T10:00:00Z",
    updatedAt: "2026-06-25T11:00:00Z",
  },
]

const FILTROS: { value: "all" | StatusProposta; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "rascunho", label: "Rascunhos" },
  { value: "enviada", label: "Enviadas" },
  { value: "aceita", label: "Aceitas" },
  { value: "recusada", label: "Recusadas" },
]

function diasAte(data: string) {
  const alvo = new Date(`${data}T23:59:59`).getTime()
  return Math.ceil((alvo - Date.now()) / 86_400_000)
}

/** Mensalidade "principal" da proposta: a da modalidade escolhida/recomendada, senão dos itens. */
function mensalDaProposta(p: Proposta) {
  const mods = p.modalidades ?? []
  if (mods.length) {
    const ref = mods.find((m) => m.id === p.modalidadeEscolhida) ?? mods.find((m) => m.recomendada) ?? mods.find((m) => m.valorMensal > 0)
    return ref?.valorMensal ?? 0
  }
  return totaisDosItens(p.itens).mensal
}

function Kpi({ label, value, detail, icon: Icon, tone }: { label: string; value: string; detail: string; icon: typeof TrendingUp; tone: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-muted-foreground">{label}</span>
        <span className={cn("grid h-7 w-7 place-items-center rounded-md", tone)}><Icon className="h-3.5 w-3.5" /></span>
      </div>
      <div className="mt-3 text-[22px] font-semibold leading-none tracking-tight tabular-nums text-foreground">{value}</div>
      <div className="mt-1.5 truncate text-[12px] text-muted-foreground">{detail}</div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

const Propostas = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [propostas, setPropostas] = useState<Proposta[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState<"all" | StatusProposta>("all")
  const [editorOpen, setEditorOpen] = useState(searchParams.get("action") === "new")
  const [editingProposta, setEditingProposta] = useState<Proposta | null>(null)
  const [viewProposta, setViewProposta] = useState<Proposta | null>(null)
  const [deleting, setDeleting] = useState<Proposta | null>(null)
  const [comContrato, setComContrato] = useState<Set<string>>(new Set())

  useEffect(() => {
    supabase.from("contratos").select("proposta_id").not("proposta_id", "is", null).then(({ data }) => {
      setComContrato(new Set((data ?? []).map((c) => c.proposta_id as string)))
    })
  }, [])

  useEffect(() => {
    const fetchPropostas = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase.from("propostas").select("*").order("created_at", { ascending: false })
        if (error) throw error
        if (data && data.length > 0) {
          const mapped = data.map(propostaFromRow)
          setPropostas(mapped)
          localStorage.setItem("itacondo_propostas", JSON.stringify(mapped))
        } else {
          const raw = localStorage.getItem("itacondo_propostas")
          setPropostas(raw ? JSON.parse(raw) : MOCK_PROPOSTAS)
        }
      } catch (err) {
        console.error("Erro ao buscar propostas:", err)
        const raw = localStorage.getItem("itacondo_propostas")
        setPropostas(raw ? JSON.parse(raw) : MOCK_PROPOSTAS)
      } finally {
        setLoading(false)
      }
    }
    fetchPropostas()
  }, [])

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setEditingProposta(null)
      setEditorOpen(true)
    }
  }, [searchParams])

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: propostas.length }
    for (const p of propostas) c[p.status] = (c[p.status] ?? 0) + 1
    return c
  }, [propostas])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return propostas.filter((p) => {
      const matchSearch = !q || [p.titulo, p.clienteNome, p.clienteEmail, p.clienteEmpresa].some((v) => v?.toLowerCase().includes(q))
      return matchSearch && (filterStatus === "all" || p.status === filterStatus)
    })
  }, [propostas, search, filterStatus])

  const kpis = useMemo(() => {
    const abertas = propostas.filter((p) => p.status === "enviada")
    const aceitas = propostas.filter((p) => p.status === "aceita")
    const decididas = aceitas.length + (counts.recusada ?? 0)
    return {
      pipeline: abertas.reduce((s, p) => s + p.valorFinal, 0),
      abertas: abertas.length,
      fechado: aceitas.reduce((s, p) => s + p.valorFinal, 0),
      aceitas: aceitas.length,
      mrr: [...abertas, ...aceitas].reduce((s, p) => s + mensalDaProposta(p), 0),
      conversao: decididas ? Math.round((aceitas.length / decididas) * 100) : 0,
      decididas,
    }
  }, [propostas, counts])

  // ── Handlers ──
  const upsertLocal = (data: Proposta) =>
    setPropostas((prev) => {
      const idx = prev.findIndex((p) => p.id === data.id)
      if (idx < 0) return [data, ...prev]
      const next = [...prev]
      next[idx] = data
      return next
    })

  const handleSave = async (data: Proposta) => {
    try {
      const { modalidadesNoBanco } = await salvarProposta(data)
      upsertLocal({ ...data, updatedAt: new Date().toISOString() })
      setEditorOpen(false)
      setEditingProposta(null)
      toast({
        title: "Proposta salva",
        description: modalidadesNoBanco || !data.modalidades?.length
          ? `"${data.titulo}" salva com sucesso.`
          : "Salva. As modalidades ficaram neste navegador até a migration do banco ser aplicada.",
      })
    } catch (err) {
      console.error("Erro ao salvar proposta:", err)
      toast({ title: "Erro ao salvar", description: (err as Error).message || "Tente novamente.", variant: "destructive" })
    }
  }

  const openEditor = (p: Proposta | null) => {
    setViewProposta(null)
    setEditingProposta(p)
    setEditorOpen(true)
  }

  const handleDuplicate = async (p: Proposta) => {
    const now = new Date().toISOString()
    const dup: Proposta = {
      ...p,
      id: uid(),
      titulo: `${p.titulo} (cópia)`,
      status: "rascunho",
      token: uid(),
      historico: [{ id: uid(), tipo: "criada", data: now, descricao: "Proposta duplicada" }],
      modalidadeEscolhida: undefined,
      aceitePor: undefined,
      aceiteEm: undefined,
      recusaPor: undefined,
      recusaEm: undefined,
      recusaMotivo: undefined,
      createdAt: now,
      updatedAt: now,
    }
    try {
      await salvarProposta(dup)
      upsertLocal(dup)
      toast({ title: "Proposta duplicada", description: `"${dup.titulo}" criada como rascunho.` })
    } catch (err) {
      toast({ title: "Erro ao duplicar", description: (err as Error).message, variant: "destructive" })
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("propostas").delete().eq("id", id)
      if (error) throw error
      setPropostas((prev) => prev.filter((p) => p.id !== id))
      toast({ title: "Proposta excluída" })
    } catch (err) {
      toast({ title: "Erro ao excluir", description: (err as Error).message, variant: "destructive" })
    }
  }

  const handleChangeStatus = async (p: Proposta, status: StatusProposta) => {
    const evento = { id: uid(), tipo: status as "enviada" | "aceita" | "recusada", data: new Date().toISOString(), descricao: `Status alterado para ${STATUS_LABELS[status]}` }
    const historico = [...(p.historico ?? []), evento]
    try {
      const { error } = await supabase.from("propostas").update({ status, historico: historico as never, updated_at: new Date().toISOString() }).eq("id", p.id)
      if (error) throw error
      upsertLocal({ ...p, status, historico, updatedAt: new Date().toISOString() })
      toast({ title: "Status atualizado", description: STATUS_LABELS[status] })
    } catch (err) {
      toast({ title: "Erro ao alterar status", description: (err as Error).message, variant: "destructive" })
    }
  }

  const handleCopyLink = (p: Proposta) => {
    navigator.clipboard.writeText(publicUrl(p)).then(
      () => toast({ title: "Link copiado", description: "Cole no WhatsApp ou e-mail do cliente." }),
      () => toast({ title: "Não foi possível copiar", variant: "destructive" }),
    )
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-foreground">Propostas</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">Monte propostas com opções de compra ou locação, envie o link e acompanhe o aceite.</p>
          </div>
          <Button onClick={() => openEditor(null)} className="h-9 gap-1.5">
            <Plus className="h-4 w-4" />Nova proposta
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 xl:grid-cols-4">
          <Kpi label="Em negociação" value={fmtCurrency(kpis.pipeline)} detail={`${kpis.abertas} proposta${kpis.abertas === 1 ? "" : "s"} aguardando resposta`} icon={Clock} tone="bg-[hsl(224_70%_16%)] text-[hsl(224_100%_78%)]" />
          <Kpi label="Fechado" value={fmtCurrency(kpis.fechado)} detail={`${kpis.aceitas} aceita${kpis.aceitas === 1 ? "" : "s"}`} icon={CheckCircle2} tone="bg-[hsl(152_45%_12%)] text-[hsl(152_60%_62%)]" />
          <Kpi label="Recorrência proposta" value={`${fmtCurrency(kpis.mrr)}`} detail="mensal em propostas abertas e aceitas" icon={Repeat} tone="bg-[hsl(258_45%_18%)] text-[hsl(258_100%_82%)]" />
          <Kpi label="Conversão" value={`${kpis.conversao}%`} detail={kpis.decididas ? `${kpis.aceitas} de ${kpis.decididas} decididas` : "nenhuma decidida ainda"} icon={TrendingUp} tone="bg-[hsl(38_45%_13%)] text-[hsl(38_92%_68%)]" />
        </div>

        {/* Table card */}
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex flex-col gap-3 border-b border-border p-3 lg:flex-row lg:items-center">
            <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Filtrar por status">
              {FILTROS.map((f) => (
                <button
                  key={f.value}
                  role="tab"
                  aria-selected={filterStatus === f.value}
                  onClick={() => setFilterStatus(f.value)}
                  className={cn(
                    "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground",
                    filterStatus === f.value && "bg-[hsl(228_8%_13%)] text-foreground",
                  )}
                >
                  {f.label}
                  <span className="rounded bg-[hsl(228_8%_16%)] px-1.5 text-[11px] tabular-nums text-muted-foreground">{counts[f.value] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="relative lg:ml-auto lg:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente, empresa ou título" className="h-8 pl-8 text-[13px]" />
            </div>
          </div>

          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-[hsl(228_8%_10%)]" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[hsl(228_8%_12%)] text-muted-foreground"><FileText className="h-5 w-5" /></span>
              <div>
                <p className="text-[14px] font-medium text-foreground">{propostas.length ? "Nenhuma proposta neste filtro" : "Nenhuma proposta ainda"}</p>
                <p className="mt-1 text-[13px] text-muted-foreground">{propostas.length ? "Ajuste a busca ou o status." : "Crie a primeira e envie o link para o cliente."}</p>
              </div>
              {!propostas.length && <Button size="sm" onClick={() => openEditor(null)} className="mt-1 gap-1.5"><Plus className="h-4 w-4" />Nova proposta</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-0">Proposta</th>
                    <th className="px-4 py-0">Condições</th>
                    <th className="px-4 py-0">Validade</th>
                    <th className="px-4 py-0">Status</th>
                    <th className="w-px px-4 py-0"><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((p) => {
                    const dias = diasAte(p.validadeAte)
                    const aberta = p.status === "rascunho" || p.status === "enviada"
                    const mods = p.modalidades ?? []
                    const mensal = totaisDosItens(p.itens).mensal
                    return (
                      <tr key={p.id} className="group cursor-pointer" onClick={() => setViewProposta(p)}>
                        <td className="px-4 py-3">
                          <div className="max-w-[320px] truncate text-[13px] font-medium text-foreground">{p.titulo}</div>
                          <div className="mt-0.5 max-w-[320px] truncate text-[12px] text-muted-foreground">
                            {p.clienteEmpresa ? `${p.clienteEmpresa} · ${p.clienteNome}` : p.clienteNome}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {mods.length > 0 ? (
                            <ModalidadesResumo modalidades={mods} escolhida={p.modalidadeEscolhida} />
                          ) : (
                            <div className="tabular-nums">
                              <div className="text-[13px] font-medium text-foreground">
                                {fmtCurrency(p.valorFinal)}
                                {p.desconto > 0 && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground line-through">{fmtCurrency(p.valorTotal)}</span>}
                              </div>
                              {mensal > 0 && <div className="text-[12px] text-[hsl(258_100%_82%)]">+ {fmtCurrency(mensal)}<span className="text-muted-foreground">/mês</span></div>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-[13px] tabular-nums text-foreground">{fmtDate(p.validadeAte)}</div>
                          {aberta && (
                            <div className={cn("text-[11px]", dias < 0 ? "text-[hsl(358_90%_72%)]" : dias <= 7 ? "text-[hsl(38_92%_66%)]" : "text-muted-foreground")}>
                              {dias < 0 ? `venceu há ${-dias} dia${dias === -1 ? "" : "s"}` : dias === 0 ? "vence hoje" : `${dias} dia${dias === 1 ? "" : "s"} restantes`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill status={p.status} />
                          {p.status === "aceita" && <div className="mt-1 text-[11px] text-muted-foreground">{comContrato.has(p.id) ? "Contrato gerado" : "Sem contrato"}</div>}
                        </td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-0.5">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => handleCopyLink(p)} aria-label="Copiar link">
                                  <Link2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copiar link do cliente</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openEditor(p)} aria-label="Editar">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" aria-label="Mais ações">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onSelect={() => setViewProposta(p)}><Eye className="mr-2 h-4 w-4" />Ver detalhes</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => window.open(publicUrl(p), "_blank")}><ExternalLink className="mr-2 h-4 w-4" />Abrir página do cliente</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleDuplicate(p)}><Copy className="mr-2 h-4 w-4" />Duplicar</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {p.status === "rascunho" && <DropdownMenuItem onSelect={() => handleChangeStatus(p, "enviada")}><Send className="mr-2 h-4 w-4" />Marcar como enviada</DropdownMenuItem>}
                                {p.status === "enviada" && <DropdownMenuItem onSelect={() => handleChangeStatus(p, "aceita")}><CheckCircle2 className="mr-2 h-4 w-4" />Marcar como aceita</DropdownMenuItem>}
                                {p.status === "enviada" && <DropdownMenuItem onSelect={() => handleChangeStatus(p, "recusada")}><XCircle className="mr-2 h-4 w-4" />Marcar como recusada</DropdownMenuItem>}
                                {p.status === "aceita" && (comContrato.has(p.id)
                                  ? <DropdownMenuItem onSelect={() => navigate("/gestao-comercial/contratos")}><FileSignature className="mr-2 h-4 w-4" />Ver contrato</DropdownMenuItem>
                                  : <DropdownMenuItem onSelect={() => navigate(`/gestao-comercial/contratos?prefill_proposta_id=${p.id}`)}><FileSignature className="mr-2 h-4 w-4" />Gerar contrato</DropdownMenuItem>)}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => setDeleting(p)} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PropostaEditor
        open={editorOpen}
        proposta={editingProposta}
        onClose={() => {
          setEditorOpen(false)
          setEditingProposta(null)
          if (searchParams.get("action")) navigate("/gestao-comercial/propostas", { replace: true })
        }}
        onSave={handleSave}
      />

      {/* Detalhes */}
      <Sheet open={!!viewProposta} onOpenChange={(o) => !o && setViewProposta(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-[hsl(228_11%_6.5%)] p-0 sm:max-w-[460px]">
          {viewProposta && (
            <div className="flex min-h-full flex-col">
              <SheetHeader className="space-y-3 border-b border-border p-5 text-left">
                <StatusPill status={viewProposta.status} className="self-start" />
                <SheetTitle className="text-[17px] font-semibold leading-snug">{viewProposta.titulo}</SheetTitle>
                <div className="text-[13px] text-muted-foreground">
                  {viewProposta.clienteNome}
                  {viewProposta.clienteEmpresa && <> · {viewProposta.clienteEmpresa}</>}
                  {viewProposta.clienteEmail && <div>{viewProposta.clienteEmail}</div>}
                  {viewProposta.clienteTelefone && <div>{viewProposta.clienteTelefone}</div>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" className="h-8 gap-1.5" onClick={() => openEditor(viewProposta)}><Pencil className="h-3.5 w-3.5" />Editar</Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => handleCopyLink(viewProposta)}><Link2 className="h-3.5 w-3.5" />Copiar link</Button>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" asChild>
                    <a href={publicUrl(viewProposta)} target="_blank" rel="noopener noreferrer" aria-label="Abrir página do cliente"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </Button>
                </div>
              </SheetHeader>

              <div className="flex-1 space-y-6 p-5">
                <section>
                  <h4 className="mb-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Condições</h4>
                  {(viewProposta.modalidades ?? []).length > 0 ? (
                    <div className="space-y-2">
                      {viewProposta.modalidades!.map((m) => (
                        <div key={m.id} className={cn("rounded-lg border p-3", viewProposta.modalidadeEscolhida === m.id ? "border-[hsl(152_60%_52%/0.5)] bg-[hsl(152_45%_12%/0.4)]" : "border-border")}>
                          <div className="flex items-center justify-between gap-2 text-[13px]">
                            <span className="font-medium text-foreground">{m.nome}</span>
                            {viewProposta.modalidadeEscolhida === m.id && <span className="text-[11px] font-medium text-[hsl(152_60%_62%)]">Escolhida pelo cliente</span>}
                            {!viewProposta.modalidadeEscolhida && m.recomendada && <span className="text-[11px] text-[hsl(224_100%_78%)]">Recomendada</span>}
                          </div>
                          <div className="mt-1 text-[13px] tabular-nums text-muted-foreground">{resumoModalidade(m)}{m.fidelidadeMeses > 0 && ` · ${m.fidelidadeMeses} meses`}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border p-3 text-[13px] tabular-nums">
                      <span className="font-medium text-foreground">{fmtCurrency(viewProposta.valorFinal)}</span>
                      {totaisDosItens(viewProposta.itens).mensal > 0 && <span className="text-muted-foreground"> + {fmtCurrency(totaisDosItens(viewProposta.itens).mensal)}/mês</span>}
                      <div className="mt-1 text-[12px] text-muted-foreground">Sem modalidades. <button className="text-[hsl(224_100%_76%)] hover:underline" onClick={() => openEditor(viewProposta)}>Adicionar compra/locação</button></div>
                    </div>
                  )}
                </section>

                {viewProposta.itens.length > 0 && (
                  <section>
                    <h4 className="mb-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Itens</h4>
                    <div className="divide-y divide-border rounded-lg border border-border">
                      {viewProposta.itens.map((item) => (
                        <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2 text-[13px]">
                          <span className="min-w-0 truncate text-foreground">{item.quantidade > 1 && <span className="text-muted-foreground">{item.quantidade}× </span>}{item.descricao}</span>
                          <span className="shrink-0 tabular-nums text-muted-foreground">{fmtCurrency(item.valorTotal)}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {viewProposta.recusaMotivo && (
                  <section>
                    <h4 className="mb-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Motivo da recusa</h4>
                    <p className="rounded-lg border border-[hsl(358_82%_62%/0.3)] bg-[hsl(358_45%_15%/0.4)] p-3 text-[13px] text-foreground">{viewProposta.recusaMotivo}</p>
                  </section>
                )}

                {(viewProposta.historico ?? []).length > 0 && (
                  <section>
                    <h4 className="mb-3 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Histórico</h4>
                    <ol className="relative space-y-4 border-l border-border pl-5">
                      {[...viewProposta.historico!].reverse().map((ev) => (
                        <li key={ev.id} className="relative">
                          <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-[hsl(228_11%_6.5%)] bg-[hsl(225_5%_45%)]" />
                          <div className="text-[13px] text-foreground">{ev.descricao}</div>
                          <div className="text-[11px] tabular-nums text-muted-foreground">{new Date(ev.data).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir proposta?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.titulo}" será removida e o link do cliente deixa de funcionar. Não dá para desfazer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && handleDelete(deleting.id)} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SuperAdminLayout>
  )
}

export default Propostas
